import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography, Container } from "@mui/material";

/**
 * Loading Spinner component
 * Displays a centered loading spinner with optional message
 * @param {Object} props Component props
 * @param {string} [props.message] Custom loading message
 * @param {number} [props.size] Size of the spinner in pixels
 * @param {boolean} [props.fullPage] Whether to display the spinner in full page mode
 * @param {string} [props.color] Color of the spinner
 * @returns {JSX.Element} The LoadingSpinner component
 */
const LoadingSpinner = ({
  message,
  size = 40,
  fullPage = false,
  color = "primary",
}) => {
  const { t } = useTranslation();
  const defaultMessage = t("Loading...");

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        minHeight: fullPage ? "80vh" : "inherit",
      }}
    >
      <CircularProgress size={size} color={color} thickness={4} />
      {(message || defaultMessage) && (
        <Typography variant="body1" color="text.secondary" align="center">
          {message || defaultMessage}
        </Typography>
      )}
    </Box>
  );

  return fullPage ? <Container maxWidth="sm">{content}</Container> : content;
};

LoadingSpinner.propTypes = {
  message: PropTypes.string,
  size: PropTypes.number,
  fullPage: PropTypes.bool,
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
    "inherit",
  ]),
};

export default LoadingSpinner;
